/**
 * The only third-party code the site loads, and the only place that loads it.
 *
 * Both entries are HighLevel (LeadConnector) products. Nothing here runs until
 * something calls it, and the callers all check src/lib/consent.ts first. The
 * two hosts below are the only ones this file asks for.
 *
 * They are not the only ones a visitor ends up talking to. Measured after
 * "Accept all" (scripts-local/verify-consent-network.mjs), the widget goes on
 * to reach stcdn.leadconnectorhq.com, services.leadconnectorhq.com,
 * services.msgsndr.com, assets.cdn.filesafe.space and fonts.bunny.net on its
 * own. That is the widget's code, not ours, and none of it happens before a
 * yes; the cookie policy names all six because a list that stops at the two we
 * type here would be an accurate description of this file and a misleading
 * description of the page.
 */

const CHAT_WIDGET_ID = "6a713309f84104f943e108ca";
const CHAT_LOADER_URL = "https://widgets.leadconnectorhq.com/loader.js";
const CHAT_RESOURCES_URL =
  "https://widgets.leadconnectorhq.com/chat-widget/loader.js";
/** HighLevel's iframe resizer. Only the booking calendar needs it. */
const BOOKING_RESIZER_URL = "https://link.msgsndr.com/js/form_embed.js";

function injectOnce(src: string, attrs?: Record<string, string>) {
  if (typeof document === "undefined") return;
  if (document.querySelector(`script[src="${src}"]`)) return;
  const script = document.createElement("script");
  script.src = src;
  script.async = true;
  if (attrs)
    for (const [key, value] of Object.entries(attrs))
      script.setAttribute(key, value);
  document.body.appendChild(script);
}

let chatLoaded = false;

export function loadChatWidget(): void {
  if (chatLoaded) return;
  chatLoaded = true;
  injectOnce(CHAT_LOADER_URL, {
    "data-resources-url": CHAT_RESOURCES_URL,
    "data-widget-id": CHAT_WIDGET_ID,
  });
  collapsePrompt();
}

/**
 * A stored withdrawal needs a fresh document so an already-loaded external
 * widget cannot keep running after the visitor turns embeds off.
 */
export function reloadWithoutEmbeds(): void {
  if (typeof window !== "undefined") window.location.reload();
}

/**
 * The widget opens a large proactive prompt the moment it loads, and it stays
 * open. This used to be dismissed on phones only, so on a desktop it sat over
 * the page at every scroll position: over the third pricing card and its
 * feature list, over the hero card's Book chip at 1100 wide, over the peak's
 * follow-up panel. A chat widget that a visitor has not asked for opens when
 * they click it, at every width. The button lives inside the widget's shadow
 * root, which is why this polls rather than waiting on an event.
 */
function collapsePrompt() {
  if (typeof window === "undefined") return;
  let attempts = 0;
  const timer = window.setInterval(() => {
    const widget = document.querySelector("chat-widget");
    const close = widget?.shadowRoot?.querySelector<HTMLElement>(
      ".lc_text-widget_prompt--prompt-close",
    );
    if (close) {
      close.click();
      window.clearInterval(timer);
    } else if (++attempts >= 40) {
      window.clearInterval(timer);
    }
  }, 250);
}

export function loadBookingResizer(): void {
  injectOnce(BOOKING_RESIZER_URL);
}
